"use client";

import * as React from "react";
import { Calendar, User, Tag, ArrowLeft, Share2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";



function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl animate-pulse space-y-8 py-12">
        <div className="h-8 w-32 rounded-none bg-muted"></div>
        <div className="h-16 w-3/4 rounded-none bg-muted"></div>
        <div className="flex gap-4">
          <div className="h-6 w-24 rounded-none bg-muted"></div>
          <div className="h-6 w-24 rounded-none bg-muted"></div>
        </div>
        <div className="h-[400px] w-full rounded-none bg-muted"></div>
        <div className="space-y-4">
          <div className="h-4 w-full rounded-none bg-muted"></div>
          <div className="h-4 w-full rounded-none bg-muted"></div>
          <div className="h-4 w-5/6 rounded-none bg-muted"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl py-24 text-center">
        <h2 className="font-default text-2xl font-bold text-destructive">Fehler beim Laden</h2>
        <p className="mt-2 text-muted-foreground">{error}</p>
        <Link to="/HomePage" className="mt-6 inline-block">
          <Button variant="outline" className="rounded-none border-foreground">Zurück zum Blog</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  if (!post) {
    return (
      <div className="mx-auto max-w-4xl py-24 text-center">
        <h2 className="font-default text-2xl font-bold">Essay nicht gefunden</h2>
        <p className="mt-2 text-muted-foreground">Der gesuchte Text existiert leider nicht in unserem Gedankengänge.</p>
        <Link to="/HomePage" className="mt-6 inline-block">
          <Button variant="outline" className="rounded-none border-foreground">Zurück zum Blog</Button>
        </Link>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-4xl">
      {}
      <header className="mb-10 space-y-6 text-center">
        <div className="flex items-center justify-center gap-2 text-sm font-medium text-ring">
          {post.categories && post.categories.length > 0 && (
            <div className="flex items-center gap-2">
              {post.categories.map((cat, i) => (
                <span key={cat.id} data-index={i} className="uppercase tracking-widest font-sans">
                  {cat.name}
                </span>
              ))}
            </div>
          )}
        </div>

        <h1 className="font-serif text-4xl font-bold leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground font-sans">
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="size-4" />
              <span>{post.author.name}</span>
            </div>
          )}
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("de-CH", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-none shadow-none">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-[21/9] w-full object-cover transition-transform duration-1000 hover:scale-105"
          />
        </div>
      )}

      {}
      <div className="prose prose-lg prose-neutral dark:prose-invert mx-auto max-w-3xl font-sans leading-relaxed text-foreground">
        {post.content ? (
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        ) : (
          <p className="text-muted-foreground italic font-serif">Kein Inhalt für diesen Essay verfügbar.</p>
        )}
      </div>

      {}
      <div className="mx-auto mt-12 flex max-w-3xl flex-col items-center justify-between gap-6 sm:flex-row">
        <div className="flex flex-wrap items-center gap-2">
          <Tag className="size-4 text-muted-foreground" />
          {post.tags && post.tags.length > 0 ? (
            post.tags.map((tag, i) => (
              <span
                key={tag.id}
                data-index={i}
                className="rounded-none bg-accent px-3 py-1 text-xs font-medium text-accent-foreground font-sans"
              >
                {tag.name}
              </span>
            ))
          ) : (
            <span className="text-sm text-muted-foreground font-sans">Keine Schlagworte</span>
          )}
        </div>
        <Button variant="outline" size="sm" className="gap-2 rounded-none border-foreground font-sans">
          <Share2 className="size-4" />
          Essay teilen
        </Button>
      </div>

      <Separator className="mx-auto my-12 max-w-3xl bg-border" />

      {}
      {post.author && (
        <div className="mx-auto max-w-3xl rounded-none bg-muted/30 p-8 sm:flex sm:items-center sm:gap-6">
          <Avatar className="mb-4 size-20 border-none shadow-none sm:mb-0 rounded-none">
            <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
            <AvatarFallback className="text-xl rounded-none bg-accent text-accent-foreground">{post.author.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <h3 className="font-serif text-xl font-bold text-foreground">
              Von {post.author.name}
            </h3>
            <p className="mt-2 text-muted-foreground font-sans text-sm leading-relaxed">
              Persönliche Reflexionen über Philosophie, Gesellschaft und den Fluss der Gedanken.
            </p>
            {post.author.link && (
              <Link to={post.author.link} className="mt-4 inline-block text-sm font-medium text-ring hover:underline font-sans">
                Alle Essays von {post.author.name} →
              </Link>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

function RelatedPostsGrid() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-[300px] animate-pulse rounded-none bg-muted"></div>
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null; 
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.slice(0, 3).map((post, i) => (
        <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden border-none bg-transparent shadow-none transition-all">
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden rounded-none">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="size-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          )}
          <CardHeader className="px-0 pb-4 pt-6">
            <div className="mb-2 flex items-center gap-2 text-xs font-medium text-ring uppercase tracking-widest font-sans">
              {post.categories?.[0]?.name || "Essay"}
            </div>
            <CardTitle className="font-serif text-xl leading-tight group-hover:text-ring transition-colors">
              <Link to={post.link || "#"} className="before:absolute before:inset-0">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="mt-auto px-0 pb-0">
            <div className="flex items-center gap-2 text-sm text-muted-foreground font-sans">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {post.date ? new Date(post.date).toLocaleDateString("de-CH", { month: "short", day: "numeric", year: "numeric" }) : "Aktuell"}
              </time>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}



export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== "undefined" && (window as any).wvcClient 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post", per_page: 1 };
    
  const mainPostQuery = new WP_Query(currentWPQueryParams);
  
  
  const relatedPostsQuery = new WP_Query({ post_type: "post", per_page: 3 });

  return (
    <section data-section-id="240659" id="single-post-template-section" className="bg-background py-16 md:py-24 text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <div className="mb-8">
          <Link to="/HomePage">
            <Button variant="ghost" className="text-muted-foreground hover:text-foreground rounded-none font-sans">
              <ArrowLeft className="mr-2 size-4" />
              Zurück zur Übersicht
            </Button>
          </Link>
        </div>

        {}
        <PostsProvider wp_query={mainPostQuery}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="mt-24 border-t border-border pt-16">
          <div className="mb-10 flex items-center justify-between">
            <h2 className="font-serif text-3xl font-bold text-foreground">Weitere Gedanken</h2>
            <Link to="/HomePage">
              <Button variant="outline" className="hidden sm:flex rounded-none border-foreground font-sans">Alle Essays lesen</Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsGrid />
          </PostsProvider>
          
          <div className="mt-8 flex justify-center sm:hidden">
            <Link to="/HomePage" className="w-full">
              <Button variant="outline" className="w-full rounded-none border-foreground font-sans">Alle Essays lesen</Button>
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}